"use client";

/**
 * useUnifiedPositions - Fetches and merges Polymarket + Kalshi positions
 *
 * Normalizes both sources to a common UnifiedPosition type.
 * Uses useDFlowRealtimePrice for live Kalshi prices.
 * Returns per-source summaries for the balance breakdown.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUserMe } from "@/contexts/UserMeContext";
import { isPredictAllowed } from "@/lib/predict-access";
import { isActiveDust } from "../dust";
import { useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useDFlowRealtimePrice, type DFlowPriceData } from "@/hooks/useDFlowRealtimePrice";
import { useDFlowPositionsQuery } from "@/hooks/useDFlowPositionsQuery";
import type { DFlowPosition } from "@/hooks/useDFlowTrading";
import { sourceUrl, extractKalshiEventTicker, marketPageUrl } from "@/lib/source-url";
import { useT } from "@/hooks/useT";
import { createLogger } from "@/lib/logger";

const logger = createLogger("useUnifiedPositions");

// ─── Types ────────────────────────────────────────────────────────────────────

export type PositionSource = "polymarket" | "kalshi" | "predict" | "hyperliquid";

export interface UnifiedPosition {
  id: string;
  source: PositionSource;
  marketTitle: string;
  marketSubtitle?: string;
  marketImage: string | null;
  outcome: string;
  /** Native Kalshi side label ("Trump", "Above 70°F") — PM/Predict leave empty. */
  outcomeNativeLabel?: string | null;
  /** Polymarket per-sub-market variant label ("28°C", "82-83°F") for
   *  multi-outcome events. Used by the Positions UI in place of the
   *  binary Yes/No outcome chip when present. */
  groupItemTitle?: string | null;
  outcomeIndex: number;
  /** Full ordered outcome pair from the market. Source of truth for the sell
   *  modal labels (Over/Under, Up/Down, Lakers/Celtics) — never re-derive
   *  this from the single-side `outcome` string. */
  outcomes?: string[] | null;
  shares: number;
  avgPrice: number;
  currentPrice: number;
  currentValue: number;
  invested: number;
  totalPnL: number;
  pnlPercent: number;
  isOpen: boolean;
  /** Backend normalizes Kalshi {determined, amended, settled, finalized, disputed}
   *  to 'resolved'. We accept the legacy spellings too so older cached payloads
   *  still hide Sell + offer Redeem instead of letting the user trade a dead market. */
  marketStatus?: 'open' | 'closed' | 'resolved' | 'settled' | 'determined' | 'amended' | 'disputed' | 'finalized' | string;
  /** Winning side on resolved Kalshi markets. Null on open/unresolved. */
  winningOutcome?: 'yes' | 'no' | null;

  // Navigation
  href: string;

  // PM-specific (for sell modal, merge, share)
  conditionId?: string;
  tokenId?: string;
  marketSlug?: string;
  eventSlug?: string | null;
  /** Canonical event headline (gamma events[0].title) — preferred display title. */
  eventTitle?: string | null;
  unrealizedPnL?: number;
  realizedPnL?: number;

  // Kalshi-specific (for sell modal)
  kalshiTicker?: string;
  dflowTicker?: string | null;
  yesMint?: string | null;
  noMint?: string | null;

  // Copytrading
  leaderName?: string | null;
  leaderImage?: string | null;
  /** Nº de compras ejecutadas que construyeron el avg (badge overlay). null/≤1 → sin badge. */
  buyCount?: number | null;

  // Timestamps — backend returns `opened_at` of the first trade for this
  // (user, tokenId), `closed_at` set when size hits 0 (or NULL while open).
  // Used by portfolio sort options ("Date") and by future date-range
  // filters. Optional because legacy PM rows pre-DB-enrichment can lack
  // both, and Hyperliquid HL rows haven't been backfilled.
  openedAt?: string | null;
  closedAt?: string | null;
}

export interface SourceSummary {
  totalValue: number;
  totalPnL: number;
  count: number;
}

// ─── Raw types from API ───────────────────────────────────────────────────────

interface PMPosition {
  conditionId: string;
  tokenId: string;
  marketSlug: string;
  eventSlug?: string | null;
  isMultiOutcome?: boolean;
  marketQuestion: string;
  marketImage?: string;
  outcome: string;
  outcomeIndex: number;
  outcomes?: string[] | null;
  shares: number;
  avgPrice: number;
  currentPrice: number;
  invested: number;
  currentValue: number;
  unrealizedPnL: number;
  realizedPnL: number;
  totalPnL: number;
  pnlPercent: number;
  isOpen: boolean;
  leaderName?: string | null;
  openedAt?: string | null;
  closedAt?: string | null;
}

// ─── Kalshi price helper (from KalshiPortfolioView) ───────────────────────────

function getKalshiCurrentPrice(
  position: DFlowPosition,
  getPrice: (ticker: string) => DFlowPriceData | undefined
): number {
  // 1. Real-time WebSocket price (best)
  const live = getPrice(position.kalshiTicker);
  if (live) {
    const isYes = position.outcome === "yes";
    const bid = isYes ? live.yesBid : live.noBid;
    const ask = isYes ? live.yesAsk : live.noAsk;
    if (bid !== null && ask !== null) return (bid + ask) / 2;
    if (bid !== null) return bid;
    if (ask !== null) return ask;
  }
  // 2. HTTP-fetched current price (fallback)
  if ((position as any).currentPrice && (position as any).currentPrice > 0) {
    return (position as any).currentPrice;
  }
  // 3. Entry price (last resort — P&L will show 0)
  return position.avgPrice;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUnifiedPositions(statusFilter: "open" | "closed" | "all" = "open") {
  const t = useT();
  const { isReady, isLoggedIn } = useAuth();
  const { user: meUser } = useUserMe();
  const predictEnabled = isPredictAllowed((meUser as { username?: string | null } | null)?.username);
  const [pmPositions, setPmPositions] = useState<PMPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Kalshi positions from shared TanStack Query (deduplicated across components)
  const { data: kalshiPositions = [], isLoading: kalshiLoading } = useDFlowPositionsQuery();

  // PM summary from backend
  const [pmBackendSummary, setPmBackendSummary] = useState<{
    totalInvested: number;
    totalCurrentValue: number;
    totalPnL: number;
    totalPositions: number;
  } | null>(null);

  // Kalshi real-time prices
  const kalshiTickers = useMemo(
    () => kalshiPositions.map((p) => p.kalshiTicker),
    [kalshiPositions]
  );
  const { getPrice } = useDFlowRealtimePrice({
    tickers: kalshiTickers,
    enabled: kalshiTickers.length > 0,
  });

  // Predict.fun positions
  const [predictPositions, setPredictPositions] = useState<UnifiedPosition[]>([]);
  const [predictLoading, setPredictLoading] = useState(false);

  // Stale-while-revalidate guard. After the first successful fetch we
  // never flip `loading` back to true — refetches (post-trade burst,
  // 60s polling, focus refetches) keep the existing list visible while
  // the new data lands. Without this, a sell triggers 0/1.5s/4s retries
  // and each one toggles the skeleton on, producing 2-3 visible
  // "parpadeos" before the final state settles.
  const hasLoadedRef = useRef(false);
  // Current PM list length, tracked without re-creating fetchPM. Lets a
  // refetch that returns empty (PM data-api lag right after a trade) keep the
  // prior list instead of flashing the "No positions" empty state.
  const pmLenRef = useRef(0);

  const fetchPM = useCallback(async (fresh = false) => {
    if (!isLoggedIn) {
      // CRITICAL: only drop the skeleton on a GENUINE logout (auth resolved,
      // not logged in). While Privy is still initializing (`!isReady`),
      // `isLoggedIn` is briefly false even for a returning user — flipping
      // loading→false here flashed the "No active positions" empty state for
      // ~1s on refresh before the real list loaded. Keep the skeleton until
      // auth resolves; deps include isReady so this refetches once it does.
      if (isReady) setLoading(false);
      return;
    }

    try {
      if (!hasLoadedRef.current) setLoading(true);
      setError(null);

      // `fresh=true` is set by post-trade refetch handlers. Routes through
      // `?fresh=1` so the backend skips its positions Redis cache and
      // re-pulls from PM data-api — without this, after a BUY/SELL the
      // backend cache (30s TTL) gets repopulated with a pre-indexed
      // snapshot and every retry sees the same stale list.
      const qp = fresh ? "&fresh=1" : "";

      let newPositions: PMPosition[] = [];
      let summarySource: any = null;

      if (statusFilter === "all") {
        // Dos fetches en paralelo en vez de un único `status=all`:
        //   • status=open  → PM data-api EN VIVO (autoritativo para abiertas).
        //   • status=closed → tabla `positions` (is_open=false) + merge PM
        //     /closed-positions, CON enrichment (imagen, won/lost/sold, P&L
        //     realizado). El path `status=all` del backend solo mergeaba las
        //     /closed-positions de PM (10-50 items, sin orden por recencia) e
        //     IGNORABA nuestra DB → las cerradas RECIENTES (vendidas/redimidas)
        //     no aparecían en el tab Closed (incidente zeroxitadori 2026-06-12).
        const [openRes, closedRes] = await Promise.all([
          authFetch(`/api/portfolio/positions?status=open${qp}`),
          authFetch(`/api/portfolio/positions?status=closed${qp}`),
        ]);
        // El set de ABIERTAS es el estado en vivo crítico — si su fetch falla
        // (PM 502 transitorio) NO pisamos la lista actual con solo-cerradas;
        // mantenemos lo previo y dejamos que el retry/poll recupere.
        if (!openRes.ok) {
          hasLoadedRef.current = true;
          return;
        }
        const openData = await openRes.json();
        const closedData = closedRes.ok ? await closedRes.json() : null;
        const openPos: PMPosition[] = openData?.positions || [];
        const closedPos: PMPosition[] = closedData?.positions || [];
        // Dedup por tokenId: una abierta nunca se duplica como cerrada;
        // prioriza la abierta (estado en vivo).
        const openTokenIds = new Set(openPos.map((p) => p.tokenId));
        newPositions = [...openPos, ...closedPos.filter((p) => !openTokenIds.has(p.tokenId))];
        // Las stats del wallet-card son de ABIERTAS.
        summarySource = openData;
      } else {
        const pmRes = await authFetch(`/api/portfolio/positions?status=${statusFilter}${qp}`);
        if (!pmRes.ok) {
          hasLoadedRef.current = true;
          return;
        }
        const pmData = await pmRes.json();
        newPositions = pmData.positions || [];
        summarySource = pmData;
      }

      // SWR for DATA (not just `loading`): a post-trade `fresh` refetch that
      // returns empty while we still hold positions is almost always PM
      // data-api indexing lag — keep the prior list to avoid flashing the
      // empty state. The steady 60s poll (fresh=false) still reflects a
      // genuine full-exit within one cycle.
      const keepPrev = fresh && newPositions.length === 0 && pmLenRef.current > 0;
      if (!keepPrev) {
        setPmPositions(newPositions);
        pmLenRef.current = newPositions.length;
        setPmBackendSummary({
          totalInvested: summarySource?.totalInvested || 0,
          totalCurrentValue: summarySource?.totalCurrentValue || 0,
          totalPnL: summarySource?.totalPnL || 0,
          totalPositions: summarySource?.totalPositions || 0,
        });
      }
      hasLoadedRef.current = true;
    } catch (err) {
      logger.error("Failed to fetch Polymarket positions", { error: err instanceof Error ? err.message : String(err) });
      setError(t("portfolio.positions.error"));
    } finally {
      setLoading(false);
    }
  }, [isReady, isLoggedIn, statusFilter, t]);

  // Fetch Predict.fun positions (open from API, closed from DB)
  const fetchPredict = useCallback(async () => {
    if (!isLoggedIn) return;
    // Skip unless the user has Predict access (per-user allowlist or global flag).
    if (!predictEnabled) return;
    try {
      setPredictLoading(true);
      const url = statusFilter === 'closed'
        ? "/api/trading/predict/positions?status=closed"
        : "/api/trading/predict/positions";
      const res = await authFetch(url);
      if (!res.ok) return;
      const data = await res.json();
      const positions = (data.data?.positions || data.positions || []) as any[];
      const isOpen = statusFilter !== 'closed';

      const mapped: UnifiedPosition[] = positions
        .filter((p: any) => isOpen ? p.size > 0 : true)
        .map((p: any) => {
          const size = Number(p.size) || 0;
          const avgPrice = Number(p.avgPrice || p.averagePrice) || 0;
          const curPrice = Number(p.currentPrice || p.price) || avgPrice;
          const invested = size * avgPrice;
          const currentValue = Number(p.valueUsd) || size * curPrice;
          const pnl = Number(p.pnlUsd) || (currentValue - invested);
          const pnlPct = invested > 0 ? (pnl / invested) * 100 : 0;

          return {
            id: `predict-${p.marketId || p.id}-${p.outcomeIndex ?? 0}`,
            source: "predict" as const,
            marketTitle: p.marketTitle || p.title || "Predict.fun Market",
            marketImage: p.image || null,
            outcome: p.outcome || (p.outcomeIndex === 0 ? "Yes" : "No"),
            outcomeIndex: p.outcomeIndex ?? 0,
            shares: size,
            avgPrice,
            currentPrice: curPrice,
            currentValue,
            invested,
            totalPnL: pnl,
            pnlPercent: pnlPct,
            isOpen,
            href: p.categorySlug
              ? `/predictfun/event/${p.categorySlug}`
              : `/predictfun/market/${p.marketId || p.id}`,
            conditionId: p.marketId || p.id,
            marketSlug: p.categorySlug || p.marketSlug || p.marketId || p.id,
            openedAt: p.openedAt || p.opened_at || p.createdAt || p.created_at || null,
            closedAt: p.closedAt || p.closed_at || null,
          };
        });

      setPredictPositions(mapped);
    } catch (err) {
      // Non-critical (Predict.fun is feature-flagged off by default) but
      // log it so a real outage doesn't read as "user has no positions".
      logger.error("Failed to fetch Predict.fun positions", { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setPredictLoading(false);
    }
  }, [isLoggedIn, statusFilter, predictEnabled]);

  // Reset the "first load" gate when the active tab switches — Active vs
  // Closed are separate datasets and the user benefits from a skeleton
  // while we fetch the new one.
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [statusFilter]);

  useEffect(() => {
    fetchPM();
    fetchPredict();
  }, [fetchPM, fetchPredict]);

  // Refresh on trade/copytrade events. Same immediate + 1.5s + 4s
  // retry schedule the rest of the post-trade surfaces use
  // (PolymarketBalanceContext, MyPositions) so PM data-api indexing
  // lag doesn't leave just-sold positions hanging in the list until
  // the next passive poll.
  useEffect(() => {
    if (!isLoggedIn) return;
    const refreshTimeouts = new Set<ReturnType<typeof setTimeout>>();
    // `fresh=true` only on the trade-event burst — keeps the periodic
    // polling on the cached path so we don't slam the backend with
    // uncached fetches every refresh cycle.
    const refetchOnce = () => { fetchPM(true); fetchPredict(); };
    const handler = () => {
      refetchOnce();
      [1500, 4000].forEach((delay) => {
        const id = setTimeout(() => {
          refreshTimeouts.delete(id);
          refetchOnce();
        }, delay);
        refreshTimeouts.add(id);
      });
    };
    window.addEventListener("copytrade:refresh", handler);
    window.addEventListener("positions:refresh", handler);
    window.addEventListener("portfolio:refresh", handler);
    return () => {
      window.removeEventListener("copytrade:refresh", handler);
      window.removeEventListener("positions:refresh", handler);
      window.removeEventListener("portfolio:refresh", handler);
      refreshTimeouts.forEach((id) => clearTimeout(id));
    };
  }, [isLoggedIn, fetchPM, fetchPredict]);

  // Passive 30s polling so the Active tab's prices / value / P&L don't sit
  // frozen between trade events. Stays on the cached backend path (fresh=false)
  // so the load is bounded to ~1 rebuild per user per 30s regardless of poll
  // cadence (backend Redis TTL is 30s). Paused when the tab is hidden — no
  // point burning network on a background page.
  useEffect(() => {
    if (!isLoggedIn) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => { fetchPM(); fetchPredict(); }, 30_000);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isLoggedIn, fetchPM, fetchPredict]);

  // ─── Normalize to UnifiedPosition ──────────────────────────────────────────

  const positions: UnifiedPosition[] = useMemo(() => {
    const pm: UnifiedPosition[] = pmPositions.map((p) => {
      // The /api/portfolio/positions response is a UNION of Polymarket
      // and Hyperliquid rows (the backend's mergeHlIntoResponse fan-out).
      // Each HL row arrives with `source: 'hyperliquid'` already stamped
      // on it. Hardcoding source: 'polymarket' for all rows was the
      // root cause of every "HL position acts like PM" bug — wrong
      // logo, /polymarket/event/<hl-slug> link, PM sell modal, etc.
      const stampedSource: PositionSource = (p as any)?.source === 'hyperliquid' ? 'hyperliquid' : 'polymarket';
      const idPrefix = stampedSource === 'hyperliquid' ? 'hl' : 'pm';
      return {
        id: `${idPrefix}-${p.conditionId || p.tokenId}-${p.tokenId}`,
        source: stampedSource,
        marketTitle: p.marketQuestion,
        marketImage: p.marketImage || null,
        outcome: p.outcome,
        groupItemTitle: (p as any).groupItemTitle ?? null,
        // 2026-05-26: backend SI emite `isMultiOutcome` en el response
        // (portfolio.ts:1725 lo enriquece por bulk con un slug-count
        // lookup en markets_cache), pero hasta ahora NO se mapeaba aquí.
        // Sin esto, el render de PositionsSection.tsx:205 cae al
        // fallback `outcomeIndex === 0 ? 'Yes' : 'No'` para markets
        // multi-outcome (NYC temperature, election odds, etc.) — el
        // user veía "Yes/No" en lugar de la variante real
        // ("82-83°F", "Trump"). Fallback heurístico: slug del market
        // distinto del event_slug = multi-outcome (Lección Feb 2026 en
        // CLAUDE.md).
        isMultiOutcome: (p as any).isMultiOutcome ?? (
          !!(p.eventSlug && p.marketSlug && p.eventSlug !== p.marketSlug)
        ),
        outcomeIndex: p.outcomeIndex,
        outcomes: (p as any).outcomes ?? null,
        shares: p.shares,
        avgPrice: p.avgPrice,
        currentPrice: p.currentPrice,
        currentValue: p.currentValue,
        invested: p.invested,
        totalPnL: p.totalPnL,
        pnlPercent: p.pnlPercent,
        isOpen: p.isOpen,
        // Propagate resolution data so closed PM positions can show WON/LOST
        // (not SOLD). Without these the row had no marketStatus/winningOutcome
        // and every closed PM position fell through to "sold".
        marketStatus: (p as any).marketStatus,
        winningOutcome: (p as any).winningOutcome ?? null,
        eventSlug: (p as any).eventSlug ?? null,
        eventTitle: (p as any).eventTitle ?? null,
        // Always prefer event page when eventSlug is known — /market stays only for SEO
        href: marketPageUrl({
          marketSlug: p.marketSlug,
          eventSlug: p.eventSlug,
          outcome: p.outcome,
          source: stampedSource,
        }),
        conditionId: p.conditionId,
        tokenId: p.tokenId,
        marketSlug: p.marketSlug,
        unrealizedPnL: p.unrealizedPnL,
        realizedPnL: p.realizedPnL,
        leaderName: p.leaderName,
        leaderImage: (p as any).leaderImage ?? null,
        buyCount: (p as any).buyCount ?? null,
        openedAt: (p as any).openedAt ?? null,
        closedAt: (p as any).closedAt ?? null,
      };
    });

    const kalshi: UnifiedPosition[] = kalshiPositions.map((p) => {
      // Backend normalizes Kalshi terminal statuses ({determined, amended,
      // settled, finalized, disputed}) to 'resolved'. Accept the raw values
      // too in case an older cached payload arrives — anything terminal hides
      // Sell and forces Redeem.
      const TERMINAL = new Set(['resolved', 'settled', 'determined', 'amended', 'finalized', 'disputed']);
      const isSettled = TERMINAL.has(String(p.marketStatus || ''));
      // Settled markets: trust the server's currentPrice verbatim — it's
      // already snapped to 1 (winner) or 0 (loser) from the Kalshi/DFlow
      // result. The previous `> 0` guard treated a legit 0 (losing
      // position) as "missing" and fell back to avgPrice, which surfaced
      // the lost stake as a ghost in the header / breakdown chip
      // (e.g. 12 shares × $0.0647 = $0.78 on a market that resolved
      // 'no' for the YES holder).
      // Live markets: keep the original cascade — server price if
      // present, else WS feed, else mid, else avg as last resort.
      const curPrice = isSettled
        ? Number(p.currentPrice ?? 0)
        : (typeof p.currentPrice === 'number' && p.currentPrice > 0
            ? p.currentPrice
            : getKalshiCurrentPrice(p, getPrice));
      const marketValue = p.size * curPrice;
      const cost = p.size * p.avgPrice;
      const pnl = marketValue - cost;
      const pnlPct = cost > 0 ? (pnl / cost) * 100 : 0;
      const eventTitle = p.eventTitle?.trim() || "";
      const marketTitle = p.marketTitle?.trim() || "";
      const hasDistinctEventTitle = !!eventTitle && eventTitle.toLowerCase() !== marketTitle.toLowerCase();
      // Collapse event + market into a single line so Kalshi cards read
      // like Polymarket's "Will X…?" single question. The outcome badge
      // below still shows the native side label, so "Claude" doesn't
      // get lost — it's just not duplicated on its own line.
      const combinedTitle = hasDistinctEventTitle
        ? `${eventTitle} · ${marketTitle}`
        : (marketTitle || eventTitle);

      return {
        id: `kalshi-${p.id}`,
        source: "kalshi" as const,
        marketTitle: combinedTitle,
        marketSubtitle: undefined,
        marketImage: p.imageUrl || null,
        outcome: p.outcome,
        outcomeNativeLabel: p.outcomeNativeLabel || null,
        outcomeIndex: p.outcome === "yes" ? 0 : 1,
        shares: p.size,
        avgPrice: p.avgPrice,
        currentPrice: curPrice,
        currentValue: marketValue,
        invested: cost,
        totalPnL: pnl,
        pnlPercent: pnlPct,
        // Settled positions are "closed" from a trading standpoint —
        // the user can no longer sell and should redeem instead.
        isOpen: !isSettled,
        marketStatus: p.marketStatus || 'open',
        winningOutcome: p.winningOutcome ?? null,
        href: sourceUrl(`/event/kalshi/${extractKalshiEventTicker(p.kalshiTicker)}`, "kalshi"),
        kalshiTicker: p.kalshiTicker,
        dflowTicker: p.dflowTicker,
        yesMint: p.yesMint,
        noMint: p.noMint,
        // Kalshi only exposes openedAt; closedAt isn't currently fed by the
        // DFlow positions endpoint. Settled positions don't get a closed_at
        // here even though the underlying market resolved.
        openedAt: p.openedAt || null,
        closedAt: (p as any).closedAt ?? null,
      };
    });

    return [...pm, ...kalshi, ...predictPositions];
  }, [pmPositions, kalshiPositions, predictPositions, getPrice]);

  // ─── Per-source summaries ──────────────────────────────────────────────────

  const pmSummary: SourceSummary = useMemo(() => {
    // Only OPEN positions belong in the wallet-card "X open positions"
    // chip. Closed rows still travel through the response (so Closed tab
    // can render them) but counting them here surfaced the misleading
    // "12 Polymarket positions" on zeroxitadori's deposit card while
    // his "0 open" hero number disagreed. Same fix applied to Kalshi
    // and Hyperliquid summaries below.
    const pmOnly = positions.filter((p) => p.source === "polymarket" && p.isOpen && !isActiveDust(p));
    return {
      totalValue: pmOnly.reduce((s, p) => s + p.currentValue, 0),
      totalPnL: pmOnly.reduce((s, p) => s + p.totalPnL, 0),
      count: pmOnly.length,
    };
  }, [positions]);

  const kalshiSummary: SourceSummary = useMemo(() => {
    const kOnly = positions.filter((p) => p.source === "kalshi" && p.isOpen && !isActiveDust(p));
    return {
      totalValue: kOnly.reduce((s, p) => s + p.currentValue, 0),
      totalPnL: kOnly.reduce((s, p) => s + p.totalPnL, 0),
      count: kOnly.length,
    };
  }, [positions]);

  const predictSummary: SourceSummary = useMemo(() => {
    const pOnly = positions.filter((p) => p.source === "predict" && p.isOpen && !isActiveDust(p));
    return {
      totalValue: pOnly.reduce((s, p) => s + p.currentValue, 0),
      totalPnL: pOnly.reduce((s, p) => s + p.totalPnL, 0),
      count: pOnly.length,
    };
  }, [positions]);

  // HL rows enter `positions` via mergeHlIntoResponse on the backend
  // (stamped with source:'hyperliquid'). Surface them as a peer summary
  // alongside PM/Kalshi/Predict so the portfolio's wallet card section
  // can render an HL row driven by the same numbers as the rest of the
  // page. Pure derivation — no extra fetch.
  const hyperliquidSummary: SourceSummary = useMemo(() => {
    const hOnly = positions.filter((p) => p.source === "hyperliquid" && p.isOpen && !isActiveDust(p));
    return {
      totalValue: hOnly.reduce((s, p) => s + p.currentValue, 0),
      totalPnL: hOnly.reduce((s, p) => s + p.totalPnL, 0),
      count: hOnly.length,
    };
  }, [positions]);

  const refetch = useCallback(() => {
    fetchPM();
    fetchPredict();
    queryClient.invalidateQueries({ queryKey: ["dflow", "positions"] });
  }, [fetchPM, fetchPredict, queryClient]);

  return {
    positions,
    pmPositions: pmPositions,
    kalshiPositions: kalshiPositions,
    predictPositions: predictPositions,
    pmSummary,
    kalshiSummary,
    predictSummary,
    hyperliquidSummary,
    pmBackendSummary,
    loading: loading || kalshiLoading || predictLoading,
    error,
    refetch,
  };
}
